// Portal Product Search API Service - product catalog and pricing lookup

import api from './api';
import type {
  DetectedPricingSource,
  PricingSourceOption,
} from './portalOrders';

export interface ProductSearchResult {
  id: string;
  sage_item_code: string;
  item_code_desc: string | null;
  udf_fulldesc: string | null;
  udf_category: string | null;
  udf_group: string | null;
  udf_subcategory: string | null;
  product_line: string | null;
  standard_unit_price: string | null;
  sales_promotion_price: string | null;
  sales_unit_of_measure: string | null;
  total_quantity_on_hand: string | null;
  ship_weight: string | null;
  /**
   * User-defined ship weight (Sage CI_Item.UDF_SHIP_WGT_LBS). Preferred
   * over {@link ship_weight} because Sage's freight estimate API keys
   * off this field, so using it keeps portal weight totals aligned with
   * the sales-site freight quote. May be null on older catalog rows —
   * callers should fall back to {@link ship_weight} in that case.
   */
  udf_ship_wgt_lbs: string | null;
  inactive_item: string | null;
  /**
   * Active-make sheet status ('active' | 'MTO' | 'inactive' | ...).
   * Null when the item is not on the sheet or sheet data is unavailable.
   * 'inactive' means PJ will not make/ship the item — the search
   * dropdown shows an amber "inactive" tag for these.
   */
  sheet_status?: string | null;
  /**
   * True when the searched customer has SP/quote pricing for this item
   * (only set when the search was customer-scoped; null/undefined otherwise).
   */
  has_sp_or_quote_pricing?: boolean | null;
  /**
   * Two-part-kit anatomy for the pickers (set by /portal/products/search,
   * NOT gated on price): the part letter ('A' | 'B' | 'C') when this SKU is a
   * kit COMPONENT, and the same-family kit parent (UOM=KIT) when the catalog
   * has one. A CSR keying the component instead of the kit was the AU01042 /
   * AU02050 miss — see KitComponentBadge.
   */
  kit_component_part?: string | null;
  kit_parent_code?: string | null;
  /**
   * The brand / customer alias phrase that pulled this SKU into the
   * results (e.g. "White Cap Canada" on an FV*NCA private-label SKU whose
   * description never mentions the brand). Null/undefined when the SKU
   * matched on its own text or code — see BrandAliasBadge.
   */
  matched_alias?: string | null;
}

export interface ProductSearchResponse {
  products: ProductSearchResult[];
  total: number;
}

export interface CustomerPricingResult {
  item_code: string;
  special_price: number | null;
  note: string | null;
  is_active: boolean | null;
}

export interface CustomerPricingResponse {
  customer_no: string;
  prices: CustomerPricingResult[];
}

export interface ResolvedPriceInfo {
  resolved_price: string | null;
  source: string;
  quote_id: number | null;
  product_hybrid_offered: string | null;
  investigation_reason: string | null;
  /**
   * Which known source ``resolved_price`` matches (backend-classified,
   * same rules as the saved-line PricingSourcePicker). Null when the
   * backend could not classify (older API, or enumeration failed).
   */
  current_source?: DetectedPricingSource | null;
  /**
   * Every pricing source on file for this item × customer × quantity
   * (SP flat/tier, each open quote, standard), with ``is_active`` on the
   * one equal to ``resolved_price``. Empty when nothing is on file or the
   * enumeration failed — never a reason to withhold the resolved price.
   */
  available_sources?: PricingSourceOption[];
}

export interface ProductWithPricingResponse {
  product: ProductSearchResult | null;
  special_pricing: CustomerPricingResult | null;
  /**
   * Authoritative price from the full resolve_price waterfall. Only
   * populated when the lookup was called with a quantity. When present,
   * prefer this over {@link ProductSearchResult.standard_unit_price}
   * and {@link CustomerPricingResult.special_price}. When
   * ``source === 'investigation'`` the waterfall could not find an
   * authoritative price — callers should NOT seed a unit_price and
   * should force operator entry instead.
   */
  resolved: ResolvedPriceInfo | null;
}

export const portalProductService = {
  /**
   * Search products by item code or description
   */
  searchProducts: async (
    query: string,
    params?: { limit?: number; offset?: number; active_only?: boolean },
  ): Promise<ProductSearchResponse> => {
    const response = await api.get<ProductSearchResponse>(
      '/portal/products/search',
      { params: { q: query, ...params } },
    );
    return response.data;
  },

  /**
   * Get customer-specific special pricing
   */
  getCustomerPricing: async (
    customerNo: string,
    itemCodes?: string[],
  ): Promise<CustomerPricingResponse> => {
    const params: Record<string, string> = {};
    if (itemCodes?.length) {
      params.item_codes = itemCodes.join(',');
    }
    const response = await api.get<CustomerPricingResponse>(
      `/portal/products/customer-pricing/${encodeURIComponent(customerNo)}`,
      { params },
    );
    return response.data;
  },

  /**
   * Lookup single product with customer-specific pricing.
   *
   * Pass ``quantity`` to also receive the authoritative price from the
   * full resolve_price waterfall (quote → SP → hybrid → investigation).
   * The UI must prefer ``response.resolved.resolved_price`` when it
   * exists — using ``standard_unit_price`` as a fallback will bypass
   * active quotes and tiered pricing.
   */
  lookupProductWithPricing: async (
    itemCode: string,
    customerNo: string,
    quantity?: string,
    orderId?: string,
  ): Promise<ProductWithPricingResponse> => {
    const params: Record<string, string> = { customer_no: customerNo };
    if (quantity !== undefined) {
      params.quantity = quantity;
    }
    // Order-scoped pricing: lets the backend apply per-ship-to pricing
    // overrides (e.g. WHITE13 → Lansing MI prices from ERSCO02's list).
    if (orderId !== undefined) {
      params.order_id = orderId;
    }
    const response = await api.get<ProductWithPricingResponse>(
      `/portal/products/lookup/${encodeURIComponent(itemCode)}`,
      { params },
    );
    return response.data;
  },
};
